import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ProtectedRoute } from '@/components/routing/ProtectedRoute'
import { RootRoute } from '@/components/routing/RootRoute'

const HomeRouter = lazy(() =>
  import('@/pages/dashboard/HomeRouter').then((m) => ({ default: m.HomeRouter })),
)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })))
const RegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
)
const EmployeeDirectoryPage = lazy(() =>
  import('@/pages/employees/EmployeeDirectoryPage').then((m) => ({ default: m.EmployeeDirectoryPage })),
)
const EmployeeProfilePage = lazy(() =>
  import('@/pages/employees/EmployeeProfilePage').then((m) => ({ default: m.EmployeeProfilePage })),
)
const OrgChartPage = lazy(() =>
  import('@/pages/organization/OrgChartPage').then((m) => ({ default: m.OrgChartPage })),
)
const OrgStructurePage = lazy(() =>
  import('@/pages/organization/OrgStructurePage').then((m) => ({ default: m.OrgStructurePage })),
)
const WorkflowsPage = lazy(() => import('@/pages/workflows/WorkflowsPage'))
const PerformancePage = lazy(() => import('@/pages/performance/PerformancePage'))
const TrainingPage = lazy(() => import('@/pages/training/TrainingPage'))
const SkillsPage = lazy(() => import('@/pages/skills/SkillsPage'))
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'))
const SurveysPage = lazy(() => import('@/pages/surveys/SurveysPage'))
const ConsultantsPage = lazy(() => import('@/pages/consultants/ConsultantsPage'))
const TimesheetsPage = lazy(() => import('@/pages/timesheets/TimesheetsPage'))
const KnowledgePage = lazy(() => import('@/pages/knowledge/KnowledgePage'))
const EquipmentPage = lazy(() => import('@/pages/equipment/EquipmentPage'))
const JobPostingsPage = lazy(() => import('@/pages/recruitment/JobPostingsPage'))
const EmployeeSurveysPage = lazy(() => import('@/pages/employee/EmployeeSurveysPage'))
const DocumentGeneratorPage = lazy(() =>
  import('@/pages/documents/DocumentGeneratorPage').then((m) => ({ default: m.DocumentGeneratorPage })),
)
const DocumentLibraryPage = lazy(() => import('@/pages/documents/DocumentLibraryPage'))
const SignatureRequestsPage = lazy(() => import('@/pages/documents/SignatureRequestsPage'))
const LeaveManagementPage = lazy(() =>
  import('@/pages/leave/LeaveManagementPage').then((m) => ({ default: m.LeaveManagementPage })),
)
const PayrollPage = lazy(() =>
  import('@/pages/payroll/PayrollPage').then((m) => ({ default: m.PayrollPage })),
)
const RecruitmentPipelinePage = lazy(() => import('@/pages/recruitment/RecruitmentPipelinePage'))
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'))
const EmployeeSpacePage = lazy(() => import('@/pages/employee/EmployeeSpacePage'))
const EmployeeOnboardingPage = lazy(() => import('@/pages/employee/EmployeeOnboardingPage'))
const EmployeeAttendancePage = lazy(() => import('@/pages/employee/EmployeeAttendancePage'))
const EmployeeLeaveRequestsPage = lazy(() => import('@/pages/employee/EmployeeLeaveRequestsPage'))
const EmployeePayslipsPage = lazy(() => import('@/pages/employee/EmployeePayslipsPage'))
const EmployeeSelfProfilePage = lazy(() => import('@/pages/employee/EmployeeSelfProfilePage'))
const ManagerDashboardPage = lazy(() => import('@/pages/manager/ManagerDashboardPage'))
const ManagerTeamPage = lazy(() => import('@/pages/manager/ManagerTeamPage'))
const ManagerValidationsPage = lazy(() => import('@/pages/manager/ManagerValidationsPage'))
const AdminOverviewPage = lazy(() => import('@/pages/admin/AdminOverviewPage'))
const AdminOrganizationsPage = lazy(() => import('@/pages/admin/AdminOrganizationsPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminStatsPage = lazy(() => import('@/pages/admin/AdminStatsPage'))
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'))
const AdminDocumentTemplatesPage = lazy(() => import('@/pages/admin/AdminDocumentTemplatesPage'))
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/AdminIntegrationsPage'))
const DataImportPage = lazy(() => import('@/pages/admin/DataImportPage'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const SubscriptionPage = lazy(() => import('@/pages/organization/SubscriptionPage'))
const OrganizationSettingsPage = lazy(() => import('@/pages/organization/OrganizationSettingsPage'))
const MissionsPage = lazy(() => import('@/pages/missions/MissionsPage'))
const MessagingPage = lazy(() => import('@/pages/messaging/MessagingPage'))
const DisciplinaryCasesPage = lazy(() => import('@/pages/employees/DisciplinaryCasesPage'))

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

export default function App() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/connexion" element={<LoginPage />} />
        <Route path="/inscription" element={<RegisterPage />} />
        <Route path="/" element={<RootRoute />} />
        <Route
          path="/tableau-de-bord"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <HomeRouter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employes"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <EmployeeDirectoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employes/:id"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager']}>
              <EmployeeProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organigramme"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <OrgChartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/structure"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <OrgStructurePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workflows"
          element={
            <ProtectedRoute allowedRoles={['org_admin']}>
              <WorkflowsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager']}>
              <PerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/formation"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <TrainingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/competences"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <SkillsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enquetes"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <SurveysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consultants"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <ConsultantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/temps-projets"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <TimesheetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connaissances"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'finance', 'manager', 'employee']}>
              <KnowledgePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/equipement"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <EquipmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offres-vivier"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <JobPostingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/enquetes"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeSurveysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'finance']}>
              <DocumentGeneratorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bibliotheque-documents"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager']}>
              <DocumentLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signatures"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager', 'finance', 'employee']}>
              <SignatureRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/abonnement"
          element={
            <ProtectedRoute allowedRoles={['org_admin']}>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parametres-organisation"
          element={
            <ProtectedRoute allowedRoles={['org_admin']}>
              <OrganizationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import-donnees"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <DataImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/conges"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <LeaveManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/paie"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'finance']}>
              <PayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recrutement"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <RecruitmentPipelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytique"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager']}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeSpacePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/integration"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/pointage"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeAttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/conges"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeLeaveRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/bulletins"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeePayslipsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-personnel/profil"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeSelfProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-manager"
          element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-manager/equipe"
          element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerTeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/espace-manager/validations"
          element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerValidationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/organisations"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminOrganizationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/utilisateurs"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/statistiques"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminStatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/audit"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/modeles-documents"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminDocumentTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-ataaba/integrations"
          element={
            <ProtectedRoute allowedRoles={['super_admin_ataaba']}>
              <AdminIntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute allowedRoles={['org_admin']}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/missions"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager', 'finance', 'employee']}>
              <MissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messagerie"
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'hr_manager', 'manager', 'finance', 'employee']}>
              <MessagingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers-disciplinaires"
          element={
            <ProtectedRoute allowedRoles={['org_admin']}>
              <DisciplinaryCasesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute
              allowedRoles={['org_admin', 'hr_manager', 'manager', 'finance', 'employee', 'super_admin_ataaba']}
            >
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
